import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Box, Typography, Paper, Tabs, Tab, Snackbar, Alert, CircularProgress } from '@mui/material';
import { useFirebaseAuthentication } from 'lib/firebase';
import { useQuestions } from './components/hooks/useQuestions';
import { useQueueManagement } from './components/hooks/useQueueManagement';
import QueueTab from './components/QueueTab';
import SentWaitingTab from './components/SentWaitingTab';
import HistoryTab from './components/HistoryTab';
import SuggestionsTab from './components/SuggestionsTab';
import SiteWrapper from '../../home-pages/SiteWrapper';
import CopyRightFour from '../../../components/footer/CopyRightFour';

const ADMIN_EMAIL = 'rdymock@gmail.com';

/**
 * Mom Stories Admin Dashboard - Main Container
 * Simplified modular architecture with component separation
 */
const MomStoriesAdminDashboard = () => {
  const user = useFirebaseAuthentication();
  const [tabValue, setTabValue] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Check if user is admin
  const isAdmin = user && user.email === ADMIN_EMAIL;

  // Custom hooks for data and operations
  const { questions, suggestions, loading } = useQuestions(isAdmin);
  const {
    handleQueueReorder,
    handlePromote,
    handleDepromote,
    handleSendNow,
    handleReject,
    handleUndoAnswer,
    getNextSunday,
  } = useQueueManagement(setSnackbar);

  // Loading state
  if (!user) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  // Access denied
  if (!isAdmin) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
      >
        <Paper elevation={8} sx={{ p: 4, maxWidth: 400, textAlign: 'center' }}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            Access Denied
          </Typography>
          <Typography variant="body1" color="text.secondary">
            You don't have permission to access this admin area.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <SiteWrapper>
      <div className="main-page-wrapper">
        <Helmet>
          <title>Mom Stories Admin</title>
        </Helmet>

        <Box sx={{ minHeight: '100vh', bgcolor: '#fff' }}>
          {/* Main Content Wrapper */}
          <Box sx={{ maxWidth: 1200, mx: 'auto', my: 4, p: 4, borderRadius: 3, bgcolor: 'grey.50' }}>
            
            {/* Header */}
            <Box sx={{ mb: 4, textAlign: 'center' }}>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  color: '#1e3a8a',
                  mb: 1,
                }}
              >
                Mom Stories Admin
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Manage weekly questions for mom's story collection
              </Typography>
            </Box>

            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} centered>
                <Tab label={`Queue (${questions.filter(q => q.status === 'queued' && q.queueOrder).length})`} />
                <Tab label={`Sent & Waiting (${questions.filter(q => q.status === 'sent').length})`} />
                <Tab label={`History (${questions.filter(q => q.status === 'answered').length})`} />
                <Tab label={`Suggestions (${suggestions.length})`} />
              </Tabs>
            </Box>

            {/* Tab Content */}
            {loading ? (
              <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {tabValue === 0 && (
                  <QueueTab
                    questions={questions}
                    onQueueReorder={handleQueueReorder}
                    onSendNow={handleSendNow}
                    onRemove={handleDepromote}
                    getNextSunday={getNextSunday}
                  />
                )}

                {tabValue === 1 && (
                  <SentWaitingTab questions={questions} />
                )}

                {tabValue === 2 && (
                  <HistoryTab 
                    questions={questions}
                    onUndoAnswer={handleUndoAnswer}
                  />
                )}

                {tabValue === 3 && (
                  <SuggestionsTab
                    suggestions={suggestions}
                    onPromote={handlePromote}
                    onReject={handleReject}
                    user={user}
                    questions={questions}
                  />
                )}
              </>
            )}
          </Box>

          {/* Footer */}
          <CopyRightFour />
        </Box>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </div>
    </SiteWrapper>
  );
};

export default MomStoriesAdminDashboard;
